import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MobileNav } from '../components/MobileNav';
import { User as UserIcon, Package, LogOut, Mail, Coins, Sparkles, Plus, Gift, History, Ticket, Lock, CheckCircle2, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

export const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [coinBalance, setCoinBalance] = useState(0);
  const [ticketBalance, setTicketBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });

  // Optional email verification
  const [emailVerified, setEmailVerified] = useState(true); // assume true until /me confirms
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyStep, setVerifyStep] = useState('send'); // 'send' | 'enter'
  const [verifyOtp, setVerifyOtp] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMaskedEmail, setVerifyMaskedEmail] = useState('');
  const [verifyDeliveryStatus, setVerifyDeliveryStatus] = useState('');
  // Customer tier
  const [tier, setTier] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchCoinBalance();
    fetchTicketBalance();
    fetchVerificationStatus();
    fetchTier();
  }, []);

  const fetchTier = async () => {
    try {
      const { data } = await axios.get(`${API}/me/tier`, { withCredentials: true });
      setTier(data);
    } catch {/* ignore */}
  };

  const fetchVerificationStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setEmailVerified(Boolean(data.email_verified));
    } catch {/* ignore */}
  };

  const sendVerification = async () => {
    setVerifyLoading(true);
    try {
      const { data } = await axios.post(`${API}/account/email/send-verification`, {}, { withCredentials: true });
      setVerifyMaskedEmail(data.masked_email);
      setVerifyDeliveryStatus(data.delivery_status);
      setVerifyStep('enter');
      toast.success(data.delivery_status === 'sent'
        ? `Kode OTP dikirim ke ${data.masked_email}`
        : 'Mode dev: OTP tercatat di Admin → OTP Logs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim OTP');
    } finally {
      setVerifyLoading(false);
    }
  };

  const submitVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email-otp`, { email: user?.email, otp: verifyOtp.trim() }, { withCredentials: true });
      toast.success('Email berhasil diverifikasi!');
      setEmailVerified(true);
      setShowVerifyModal(false);
      setVerifyOtp('');
      setVerifyStep('send');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verifikasi gagal');
    } finally {
      setVerifyLoading(false);
    }
  };

  const openVerifyModal = () => {
    setVerifyStep('send');
    setVerifyOtp('');
    setVerifyMaskedEmail('');
    setVerifyDeliveryStatus('');
    setShowVerifyModal(true);
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoinBalance = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/balance`, { withCredentials: true });
      setCoinBalance(data.coin_balance || 0);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const fetchTicketBalance = async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/balance`, { withCredentials: true });
      setTicketBalance(data.ticket_balance || 0);
    } catch (error) {
      console.error('Failed to fetch ticket balance:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logout berhasil');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error('Confirm password tidak cocok');
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      }, { withCredentials: true });
      toast.success('Password berhasil diganti');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
      setShowChangePassword(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal');
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0
  }).format(price);

  const formatCoins = (coins) => new Intl.NumberFormat('id-ID').format(coins);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('id-ID', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const quickActions = [
    { icon: Plus, label: 'Top Up', path: '/topup', testId: 'profile-topup-btn' },
    { icon: Sparkles, label: 'Lucky Draw', path: '/lucky-draw', testId: 'profile-luckydraw-btn' },
    { icon: Gift, label: 'Inventory', path: '/inventory', testId: 'profile-inventory-btn' },
    { icon: Coins, label: 'Coin History', path: '/wallet/history', testId: 'profile-wallet-history-btn' },
    { icon: History, label: 'Draw History', path: '/lucky-draw/history', testId: 'profile-draw-history-btn' },
    { icon: Ticket, label: 'Tickets', path: '/tickets/history', testId: 'profile-tickets-btn' },
    { icon: Lock, label: 'Account', path: '/account/settings', testId: 'profile-account-settings-btn' },
  ];

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-5">
          <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>My Profile</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* User card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-6 backdrop-blur-xl"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(3,32,20,0.4))', border: '1px solid rgba(212,175,55,0.3)' }}>
              <UserIcon className="w-8 h-8" style={{ color: '#D4AF37' }} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-medium" style={{ color: '#F8FAF9' }} data-testid="user-name">{user?.name}</h2>
              <div className="flex items-center gap-2 text-sm mt-1" style={{ color: '#A3B8AC' }}>
                <Mail className="w-4 h-4" />
                <span data-testid="user-email">{user?.email}</span>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {emailVerified ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }} data-testid="email-verified-badge">
                    <CheckCircle2 className="w-3 h-3" /> Email Terverifikasi
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }} data-testid="email-unverified-badge">
                      <ShieldAlert className="w-3 h-3" /> Belum Terverifikasi
                    </span>
                    <button onClick={openVerifyModal} className="text-xs font-medium underline-offset-2 hover:underline" style={{ color: '#D4AF37' }} data-testid="verify-email-btn">
                      Verifikasi Email (opsional)
                    </button>
                  </>
                )}
                {user?.role === 'admin' && (
                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.3)' }}>
                    ADMIN
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tier Badge + Benefits */}
        {tier && (() => {
          const pct = tier.next_tier_min_idr
            ? Math.min(100, Math.round((tier.lifetime_spending_idr / tier.next_tier_min_idr) * 100))
            : 100;
          const remaining = tier.next_tier_min_idr ? Math.max(0, tier.next_tier_min_idr - tier.lifetime_spending_idr) : 0;
          return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-6 backdrop-blur-xl"
              style={{ background: `linear-gradient(135deg, ${tier.color}22 0%, rgba(3,32,20,0.4) 100%)`, border: `1px solid ${tier.color}55` }}
              data-testid="profile-tier-card"
            >
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase" style={{ background: tier.color, color: '#080C0A' }} data-testid="profile-tier-badge">
                    {tier.name}
                  </span>
                  <span className="text-sm" style={{ color: '#A3B8AC' }}>Lifetime: <strong style={{ color: '#F8FAF9' }}>Rp {new Intl.NumberFormat('id-ID').format(tier.lifetime_spending_idr || 0)}</strong></span>
                </div>
                {tier.next_tier_name && (
                  <span className="text-xs" style={{ color: '#A3B8AC' }}>
                    {pct}% menuju <strong style={{ color: tier.color }}>{tier.next_tier_name}</strong>
                  </span>
                )}
              </div>
              {tier.next_tier_name && (
                <>
                  <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: tier.color }} data-testid="profile-tier-progress" />
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#A3B8AC' }}>
                    Top-up <strong style={{ color: '#D4AF37' }}>Rp {new Intl.NumberFormat('id-ID').format(remaining)}</strong> lagi untuk naik ke {tier.next_tier_name}.
                  </p>
                </>
              )}
              {tier.benefits && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-xs uppercase tracking-wider mb-1" style={{ color: tier.color }}>Benefits</p>
                  <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#CFE0D5' }} data-testid="profile-tier-benefits">{tier.benefits}</p>
                </div>
              )}
            </motion.div>
          );
        })()}

        {/* Balances */}
        <div className="grid md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-6 backdrop-blur-xl"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(3,32,20,0.4) 100%)', border: '1px solid rgba(212,175,55,0.3)' }}
            data-testid="profile-coin-balance"
          >
            <div className="flex items-center gap-3 mb-2">
              <Coins className="w-6 h-6" style={{ color: '#D4AF37' }} />
              <span className="text-sm" style={{ color: '#A3B8AC' }}>Coin Balance</span>
            </div>
            <p className="text-3xl font-light" style={{ fontFamily: playfairFont, color: '#D4AF37' }} data-testid="profile-coin-value">
              {formatCoins(coinBalance)} <span className="text-base" style={{ color: '#A3B8AC' }}>Coin</span>
            </p>
            <p className="text-xs mt-1" style={{ color: '#A3B8AC' }} data-testid="profile-coin-idr">≈ Rp {formatCoins(coinBalance * 1000)} · 1 Coin = Rp 1.000</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-6 backdrop-blur-xl"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(3,32,20,0.4) 100%)', border: '1px solid rgba(212,175,55,0.2)' }}
            data-testid="profile-ticket-balance"
          >
            <div className="flex items-center gap-3 mb-2">
              <Ticket className="w-6 h-6" style={{ color: '#D4AF37' }} />
              <span className="text-sm" style={{ color: '#A3B8AC' }}>Tickets · 0.2% Cashback</span>
            </div>
            <p className="text-3xl font-light" style={{ fontFamily: playfairFont, color: '#D4AF37' }}>
              {formatCoins(ticketBalance)} <span className="text-base" style={{ color: '#A3B8AC' }}>Tickets</span>
            </p>
            <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>≈ Rp {formatCoins(ticketBalance * 100)} · 1 ticket = Rp 100</p>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <motion.button
                key={action.path}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(action.path)}
                className="rounded-2xl p-5 backdrop-blur-xl flex flex-col items-center gap-2"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid={action.testId}
              >
                <Icon className="w-6 h-6" style={{ color: '#D4AF37' }} />
                <span className="text-sm font-medium" style={{ color: '#F8FAF9' }}>{action.label}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Orders */}
        <div>
          <h2 className="text-xl font-light mb-4 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <History className="w-5 h-5" style={{ color: '#D4AF37' }} />
            Order History
          </h2>

          {loading ? (
            <div className="text-center py-8" style={{ color: '#A3B8AC' }}>Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="rounded-2xl p-8 text-center backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <Package className="w-16 h-16 mx-auto mb-3" style={{ color: '#A3B8AC' }} />
              <p style={{ color: '#A3B8AC' }}>No orders yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <motion.div
                  key={order.id}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  className="rounded-2xl p-4 cursor-pointer backdrop-blur-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-mono font-semibold" style={{ color: '#F8FAF9' }}>#{order.id.slice(0, 8)}</p>
                      <p className="text-sm" style={{ color: '#A3B8AC' }}>{formatDate(order.created_at)}</p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>
                      {order.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm" style={{ color: '#A3B8AC' }}>{order.items.length} items</p>
                    <p className="font-bold" style={{ fontFamily: playfairFont, color: '#D4AF37' }} data-testid="order-total">{formatPrice(order.total_amount)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowChangePassword(!showChangePassword)}
          className="w-full py-4 rounded-2xl font-medium flex items-center justify-center gap-2"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }}
          data-testid="toggle-change-password-btn"
        >
          <Lock className="w-5 h-5" style={{ color: '#D4AF37' }} />
          {showChangePassword ? 'Cancel' : 'Change Password'}
        </motion.button>

        {showChangePassword && (
          <motion.form
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleChangePassword}
            className="rounded-2xl p-6 backdrop-blur-xl space-y-4"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="profile-change-password-form"
          >
            <input type="password" required placeholder="Current password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }} data-testid="profile-current-pw-input" />
            <input type="password" required minLength={6} placeholder="New password (min 6 chars)" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }} data-testid="profile-new-pw-input" />
            <input type="password" required placeholder="Confirm new password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }} data-testid="profile-confirm-pw-input" />
            <motion.button whileTap={{ scale: 0.98 }} type="submit" className="w-full py-3 rounded-full font-semibold" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="profile-change-password-submit">
              Update Password
            </motion.button>
          </motion.form>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full py-4 rounded-full font-semibold flex items-center justify-center gap-2"
          style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </motion.button>
      </div>

      <MobileNav />

      {/* ===== Optional Email Verification Modal ===== */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }} data-testid="verify-email-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md rounded-2xl p-6 relative" style={{ background: '#0c1f17', border: '1px solid rgba(212,175,55,0.3)' }}>
            <button onClick={() => setShowVerifyModal(false)} className="absolute top-4 right-4" style={{ color: '#A3B8AC' }} data-testid="close-verify-modal">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-light mb-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Verifikasi Email</h3>
            <p className="text-xs mb-5" style={{ color: '#A3B8AC' }}>
              Opsional — verifikasi mengonfirmasi kepemilikan email Anda. Semua fitur tetap bisa dipakai tanpa verifikasi.
            </p>

            {verifyStep === 'send' ? (
              <div className="space-y-4">
                <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <p className="text-xs" style={{ color: '#A3B8AC' }}>Email tujuan:</p>
                  <p className="text-sm font-medium" style={{ color: '#F8FAF9' }}>{user?.email}</p>
                </div>
                <button onClick={sendVerification} disabled={verifyLoading}
                  className="w-full rounded-full py-3 font-medium disabled:opacity-50"
                  style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="send-verification-btn">
                  {verifyLoading ? 'Mengirim...' : 'Kirim Kode OTP'}
                </button>
              </div>
            ) : (
              <form onSubmit={submitVerifyOtp} className="space-y-4">
                <p className="text-sm" style={{ color: '#CFE0D5' }}>Kode OTP dikirim ke <strong>{verifyMaskedEmail}</strong></p>
                {verifyDeliveryStatus === 'fallback' && (
                  <p className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                    Mode dev: lihat OTP di Admin → OTP Logs.
                  </p>
                )}
                <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={verifyOtp}
                  onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, ''))} required
                  className="w-full rounded-lg px-4 py-4 text-center text-2xl font-mono tracking-[0.5em] outline-none"
                  style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }}
                  placeholder="000000" data-testid="verify-otp-input" />
                <button type="submit" disabled={verifyLoading || verifyOtp.length !== 6}
                  className="w-full rounded-full py-3 font-medium disabled:opacity-50"
                  style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="submit-verify-otp-btn">
                  {verifyLoading ? 'Memverifikasi...' : 'Verifikasi'}
                </button>
                <button type="button" onClick={() => setVerifyStep('send')}
                  className="w-full text-xs hover:underline" style={{ color: '#A3B8AC' }}>
                  Kirim ulang kode
                </button>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};
